import type { ContactField, SegmentRule, Tag, TenantChannel } from '@wa/shared';

/**
 * The fields a customer can be filtered on, and what each one accepts.
 *
 * One catalogue feeds three things: the filter builder's field picker, the
 * "Choose fields" column list, and the rule → sentence rendering. Keeping them
 * on one list is what stops a field being filterable but not showable, or the
 * reverse.
 */
export type ValueKind = 'text' | 'number' | 'tags' | 'bool' | 'date' | 'channel' | 'list';

export interface FilterField {
  /** Stable key: the rule kind, or `field:<key>` for a custom field. */
  key: string;
  label: string;
  kind: SegmentRule['kind'];
  /** The custom field's key when kind is 'field'. */
  field?: string;
  value: ValueKind;
  options?: string[];
  /** Whether the field can be a table column. Everything can, today. */
  column: boolean;
}

export interface Operator { op: string; label: string; needsValue: boolean }

export const SYSTEM_FIELDS: FilterField[] = [
  { key: 'name', label: 'Name', kind: 'name', value: 'text', column: true },
  { key: 'phone', label: 'Phone Number', kind: 'phone', value: 'text', column: true },
  { key: 'tags', label: 'Tags', kind: 'tag', value: 'tags', column: true },
  { key: 'opted_out', label: 'Opted out', kind: 'opted_out', value: 'bool', column: true },
  { key: 'created', label: 'Customer Date Created', kind: 'created', value: 'date', column: true },
  { key: 'last_inbound', label: 'Last message received at', kind: 'last_inbound', value: 'date', column: true },
  { key: 'channel', label: 'Channel', kind: 'channel', value: 'channel', column: false },
];

export function catalogue(fields: ContactField[]): FilterField[] {
  return [
    ...SYSTEM_FIELDS,
    ...fields.map((f): FilterField => ({
      key: `field:${f.fieldKey}`,
      label: f.label,
      kind: 'field',
      field: f.fieldKey,
      value: f.fieldType === 'number' ? 'number' : f.fieldType === 'boolean' ? 'bool' : f.fieldType === 'list' ? 'list' : f.fieldType === 'date' ? 'text' : 'text',
      options: f.fieldType === 'list' && f.options ? f.options.split('\n').map((o) => o.trim()).filter(Boolean) : undefined,
      column: true,
    })),
  ];
}

const TEXT: Operator[] = [
  { op: 'is_set', label: 'Is set', needsValue: false },
  { op: 'is_not_set', label: 'Is not set', needsValue: false },
  { op: 'is', label: 'Equals', needsValue: true },
  { op: 'is_not', label: 'Not equals', needsValue: true },
  { op: 'contains', label: 'Contains', needsValue: true },
  { op: 'not_contains', label: 'Not contains', needsValue: true },
];
const NUMBER: Operator[] = [
  { op: 'is_set', label: 'Is set', needsValue: false },
  { op: 'is_not_set', label: 'Is not set', needsValue: false },
  { op: 'is', label: 'Equals', needsValue: true },
  { op: 'gt', label: 'Greater than', needsValue: true },
  { op: 'lt', label: 'Less than', needsValue: true },
];
const TAGS: Operator[] = [
  { op: 'is_set', label: 'Is set', needsValue: false },
  { op: 'is_not_set', label: 'Is not set', needsValue: false },
  { op: 'any_of', label: 'Any of', needsValue: true },
  { op: 'all_of', label: 'All of', needsValue: true },
  { op: 'none_of', label: 'None of', needsValue: true },
];
const BOOL: Operator[] = [{ op: 'is', label: 'Equals', needsValue: true }];
const DATE: Operator[] = [
  { op: 'in_last', label: 'In the last', needsValue: true },
  { op: 'not_in_last', label: 'Not in the last', needsValue: true },
  { op: 'after', label: 'After', needsValue: true },
  { op: 'before', label: 'Before', needsValue: true },
  { op: 'is_set', label: 'Is set', needsValue: false },
  { op: 'is_not_set', label: 'Is not set', needsValue: false },
];
const CHANNEL: Operator[] = [
  { op: 'is', label: 'Is', needsValue: true },
  { op: 'is_not', label: 'Is not', needsValue: true },
];
const LIST: Operator[] = [
  { op: 'is_set', label: 'Is set', needsValue: false },
  { op: 'is_not_set', label: 'Is not set', needsValue: false },
  { op: 'is', label: 'Equals', needsValue: true },
  { op: 'is_not', label: 'Not equals', needsValue: true },
];

export function operatorsFor(field: FilterField): Operator[] {
  switch (field.value) {
    case 'number': return NUMBER;
    case 'tags': return TAGS;
    case 'bool': return field.kind === 'opted_out' ? BOOL : LIST.filter((o) => o.op !== 'is_not');
    case 'date': return DATE;
    case 'channel': return CHANNEL;
    case 'list': return LIST;
    default: return TEXT;
  }
}

export function fieldOf(rule: SegmentRule, all: FilterField[]): FilterField | undefined {
  return rule.kind === 'field'
    ? all.find((f) => f.kind === 'field' && f.field === rule.field)
    : all.find((f) => f.kind === rule.kind);
}

export function blankRule(field: FilterField, group: number): SegmentRule {
  const op = operatorsFor(field)[0]!.op;
  return {
    kind: field.kind, op, field: field.field ?? null, group,
    value: field.kind === 'opted_out' ? 'false' : field.value === 'date' ? '72' : '',
  };
}

/** The condition as a sentence — for the chip row and the segment column's tooltip. */
export function describe(rule: SegmentRule, all: FilterField[], tags: Tag[], channels: TenantChannel[]): string {
  const field = fieldOf(rule, all);
  const label = field?.label ?? rule.kind;
  const op = field ? operatorsFor(field).find((o) => o.op === rule.op) : undefined;
  const opLabel = op?.label ?? rule.op;
  if (!op?.needsValue) return `${label} ${opLabel.toLowerCase()}`;
  let value = rule.value ?? '';
  if (field?.value === 'tags') {
    value = value.split(',').map((id) => tags.find((t) => t.id === Number(id))?.name ?? id).join(', ');
  } else if (field?.value === 'channel') {
    value = channels.find((c) => c.id === Number(value))?.verifiedName ?? value;
  } else if (field?.value === 'date' && (rule.op === 'in_last' || rule.op === 'not_in_last')) {
    const hours = Number(value);
    value = hours % 24 === 0 && hours >= 24 ? `${hours / 24} days` : `${hours} hours`;
  } else if (field?.kind === 'opted_out') {
    value = value === 'true' ? 'Yes' : 'No';
  }
  return `${label} ${opLabel.toLowerCase()} ${value}`;
}

/** Is every rule complete enough to send? The server refuses the rest. */
export function complete(rules: SegmentRule[], all: FilterField[]): boolean {
  return rules.every((r) => {
    const field = fieldOf(r, all);
    if (!field) return false;
    const op = operatorsFor(field).find((o) => o.op === r.op);
    if (!op) return false;
    return !op.needsValue || (r.value ?? '').trim() !== '';
  });
}

export function groupsOf(rules: SegmentRule[]): number[] {
  return [...new Set(rules.map((r) => r.group ?? 0))].sort((a, b) => a - b);
}
